import { EngagementMetrics, AnalyticsEvent } from '../types/enhancedAnalysis';

interface EngagementTrackerConfig {
  analysisId: string;
  userId?: string;
  sessionId: string;
  startTime: Date;
}

export class EngagementTracker {
  private config: EngagementTrackerConfig;
  private metrics: Partial<EngagementMetrics>;
  private eventQueue: AnalyticsEvent[] = [];
  private isActive: boolean = true;
  private lastActiveTime: Date;
  private scrollDepthCheckpoints: Set<number> = new Set();
  private timeSpentInterval: NodeJS.Timeout | null = null;

  constructor(config: EngagementTrackerConfig) {
    this.config = config;
    this.lastActiveTime = new Date();
    
    this.metrics = {
      sessionId: config.sessionId,
      userId: config.userId,
      analysisId: config.analysisId,
      readingProgress: 0,
      timeSpent: 0,
      scrollDepth: 0,
      readingSpeed: 0,
      sectionsViewed: [],
      methodologyViewed: false,

      bookmarked: false,
      liked: false,
      commented: false,
      practiceStarted: false,
      templateDownloaded: false,
      relatedCasesViewed: 0,
      resourcesAccessed: [],
      registrationPrompted: false,
      registrationCompleted: false,
      conversionValue: 0,
      startTime: config.startTime,
      lastActiveTime: this.lastActiveTime,
      deviceType: this.detectDeviceType(),
      referrer: document.referrer,
      userAgent: navigator.userAgent
    };

    this.initializeTracking();
  }

  private detectDeviceType(): 'desktop' | 'tablet' | 'mobile' {
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  }

  private initializeTracking() {
    // 开始时间追踪
    this.startTimeTracking();
    
    // 监听页面可见性变化
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
    
    // 监听页面卸载
    window.addEventListener('beforeunload', this.handlePageUnload.bind(this));
    
    // 监听用户活动
    this.setupActivityListeners();
    
    // 定期发送数据
    this.startPeriodicSync();
  }

  private startTimeTracking() {
    this.timeSpentInterval = setInterval(() => {
      if (this.isActive) {
        this.metrics.timeSpent = (this.metrics.timeSpent || 0) + 1;
        this.updateReadingSpeed();
      }
    }, 1000);
  }

  private updateReadingSpeed() {
    const timeInMinutes = (this.metrics.timeSpent || 0) / 60;
    const charactersRead = (this.metrics.readingProgress || 0) * 0.01 * 5000; // 假设文章5000字符
    this.metrics.readingSpeed = timeInMinutes > 0 ? charactersRead / timeInMinutes : 0;
  }

  private setupActivityListeners() {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    
    events.forEach(event => {
      document.addEventListener(event, this.handleUserActivity.bind(this), { passive: true });
    });
  }

  private handleUserActivity() {
    this.lastActiveTime = new Date();
    this.metrics.lastActiveTime = this.lastActiveTime;
    
    if (!this.isActive) {
      this.isActive = true;
      this.trackEvent({
        category: 'engagement',
        action: 'user_active',
        timestamp: new Date()
      });
    }
  }

  private handleVisibilityChange() {
    if (document.hidden) {
      this.isActive = false;
      this.trackEvent({
        category: 'engagement',
        action: 'page_hidden',
        timestamp: new Date()
      });
    } else {
      this.isActive = true;
      this.trackEvent({
        category: 'engagement',
        action: 'page_visible',
        timestamp: new Date()
      });
    }
  }

  private handlePageUnload() {
    this.metrics.endTime = new Date();
    this.syncData(true); // 强制同步
  }

  private startPeriodicSync() {
    setInterval(() => {
      this.syncData();
    }, 30000); // 每30秒同步一次
  }

  // 公共方法
  public updateProgress(progress: number) {
    const oldProgress = this.metrics.readingProgress || 0;
    this.metrics.readingProgress = Math.max(oldProgress, progress);
    
    // 检查滚动深度里程碑
    const milestones = [25, 50, 75, 90, 100];
    milestones.forEach(milestone => {
      if (progress >= milestone && !this.scrollDepthCheckpoints.has(milestone)) {
        this.scrollDepthCheckpoints.add(milestone);
        this.trackEvent({
          category: 'engagement',
          action: 'scroll_milestone',
          label: `${milestone}%`,
          value: milestone,
          timestamp: new Date()
        });
      }
    });
    
    this.updateScrollDepth(progress);
  }

  private updateScrollDepth(progress: number) {
    this.metrics.scrollDepth = Math.max(this.metrics.scrollDepth || 0, progress);
  }

  public trackSectionView(sectionId: string) {
    if (!this.metrics.sectionsViewed?.includes(sectionId)) {
      this.metrics.sectionsViewed = [...(this.metrics.sectionsViewed || []), sectionId];
      
      this.trackEvent({
        category: 'content',
        action: 'section_viewed',
        label: sectionId,
        timestamp: new Date()
      });
    }
  }

  public trackMethodologyView() {
    if (!this.metrics.methodologyViewed) {
      this.metrics.methodologyViewed = true;
      
      this.trackEvent({
        category: 'content',
        action: 'methodology_viewed',
        timestamp: new Date()
      });
    }
  }

  // 移除分享追踪方法

  public trackBookmark() {
    this.metrics.bookmarked = true;
    
    this.trackEvent({
      category: 'engagement',
      action: 'bookmark',
      timestamp: new Date()
    });
  }

  public trackLike() {
    this.metrics.liked = true;
    
    this.trackEvent({
      category: 'engagement',
      action: 'like',
      timestamp: new Date()
    });
  }

  public trackComment() {
    this.metrics.commented = true;
    
    this.trackEvent({
      category: 'engagement',
      action: 'comment',
      timestamp: new Date()
    });
  }

  public trackPracticeStart() {
    this.metrics.practiceStarted = true;
    this.metrics.conversionValue += 10; // 实践练习有较高转化价值
    
    this.trackEvent({
      category: 'conversion',
      action: 'practice_started',
      value: 10,
      timestamp: new Date()
    });
  }

  public trackTemplateDownload() {
    this.metrics.templateDownloaded = true;
    this.metrics.conversionValue += 5;
    
    this.trackEvent({
      category: 'conversion',
      action: 'template_downloaded',
      value: 5,
      timestamp: new Date()
    });
  }

  public trackRelatedCaseView() {
    this.metrics.relatedCasesViewed = (this.metrics.relatedCasesViewed || 0) + 1;
    
    this.trackEvent({
      category: 'content',
      action: 'related_case_viewed',
      value: this.metrics.relatedCasesViewed,
      timestamp: new Date()
    });
  }

  public trackResourceAccess(resourceId: string) {
    if (!this.metrics.resourcesAccessed?.includes(resourceId)) {
      this.metrics.resourcesAccessed = [...(this.metrics.resourcesAccessed || []), resourceId];
      
      this.trackEvent({
        category: 'content',
        action: 'resource_accessed',
        label: resourceId,
        timestamp: new Date()
      });
    }
  }

  public trackRegistrationPrompt() {
    if (!this.metrics.registrationPrompted) {
      this.metrics.registrationPrompted = true;
      
      this.trackEvent({
        category: 'conversion',
        action: 'registration_prompt_shown',
        value: this.metrics.readingProgress || 0,
        timestamp: new Date()
      });
    }
  }

  public trackRegistrationComplete() {
    this.metrics.registrationCompleted = true;
    this.metrics.conversionValue += 50; // 注册是最高价值转化
    
    this.trackEvent({
      category: 'conversion',
      action: 'registration_completed',
      value: 50,
      timestamp: new Date()
    });
  }

  public trackNextAction(action: string) {
    this.metrics.nextActionTaken = action;
    
    this.trackEvent({
      category: 'conversion',
      action: 'next_action_taken',
      label: action,
      timestamp: new Date()
    });
  }

  private trackEvent(event: Omit<AnalyticsEvent, 'sessionId' | 'userId' | 'analysisId'>) {
    const fullEvent: AnalyticsEvent = {
      ...event,
      sessionId: this.config.sessionId,
      userId: this.config.userId,
      analysisId: this.config.analysisId
    };
    
    this.eventQueue.push(fullEvent);
    
    // 如果队列太长，立即同步
    if (this.eventQueue.length >= 10) {
      this.syncData();
    }
  }

  private async syncData(force: boolean = false) {
    if (this.eventQueue.length === 0 && !force) return;
    
    try {
      // 这里应该调用实际的API
      const payload = {
        metrics: this.metrics,
        events: this.eventQueue
      };
      
      // 模拟API调用
      console.log('Syncing engagement data:', payload);
      
      // 发送到分析服务
      await this.sendToAnalyticsService(payload);
      
      // 清空事件队列
      this.eventQueue = [];
      
    } catch (error) {
      console.error('Failed to sync engagement data:', error);
      // 保留事件队列以便重试
    }
  }

  private async sendToAnalyticsService(payload: any) {
    // 这里实现实际的API调用
    // 可以发送到 Google Analytics, Mixpanel, 或自定义分析服务
    
    // 示例：发送到自定义API
    /*
    const response = await fetch('/api/analytics/engagement', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      throw new Error('Failed to send analytics data');
    }
    */
  }

  // 获取当前指标
  public getMetrics(): Partial<EngagementMetrics> {
    return { ...this.metrics };
  }

  public getSessionId(): string {
    return this.config.sessionId;
  }

  // 计算参与度分数
  public getEngagementScore(): number {
    const weights = {
      readingProgress: 0.3,
      timeSpent: 0.2,
      sectionsViewed: 0.15,
      interactions: 0.15,
      sharing: 0.1,
      conversion: 0.1
    };
    
    const progress = (this.metrics.readingProgress || 0) / 100;
    const timeScore = Math.min((this.metrics.timeSpent || 0) / 300, 1); // 5分钟为满分
    const sectionsScore = Math.min((this.metrics.sectionsViewed?.length || 0) / 4, 1);
    
    const interactionScore = [
      this.metrics.bookmarked,
      this.metrics.liked,
      this.metrics.commented,
      this.metrics.methodologyViewed
    ].filter(Boolean).length / 4;
    
    const sharingScore = 0; // 移除分享功能
    const conversionScore = (this.metrics.conversionValue || 0) / 50;
    
    return (
      progress * weights.readingProgress +
      timeScore * weights.timeSpent +
      sectionsScore * weights.sectionsViewed +
      interactionScore * weights.interactions +
      sharingScore * weights.sharing +
      conversionScore * weights.conversion
    );
  }

  // 销毁追踪器
  public destroy() {
    this.isActive = false;
    
    if (this.timeSpentInterval) {
      clearInterval(this.timeSpentInterval);
    }
    
    // 移除事件监听器
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('beforeunload', this.handlePageUnload);
    
    // 最后同步数据
    this.syncData(true);
  }
}

// 工厂函数
export const createEngagementTracker = (config: EngagementTrackerConfig): EngagementTracker => {
  return new EngagementTracker(config);
};